import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";

import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Store, MapPin, CheckCircle } from "lucide-react";
import { getAuthHeader } from "@/lib/authHelpers";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { detectUserCountry } from "@/lib/countryDetection";
import { useStripePrices, useShopOwnerFeatures } from "@/lib/featureFlags";

export default function ClaimShopPage() {
    const [, params] = useRoute("/claim-shop/:id");
    const [, setLocation] = useLocation();
    const { isLoggedIn, refreshAuth } = useAuth();
    const { toast } = useToast();
    const { t } = useI18n();
    const { getPrice } = useStripePrices();
    const { canSeeShopOwner } = useShopOwnerFeatures();
    const [loading, setLoading] = useState(false);
    const countryCode = detectUserCountry();

    // Redirect if shop owner features are disabled
    if (!canSeeShopOwner) {
        setLocation("/");
        return null;
    }

    const { data: shop, isLoading } = useQuery({
        queryKey: ["shop", params?.id],
        queryFn: async () => {
            const res = await fetch(`/api/shops/${params?.id}`);
            if (!res.ok) throw new Error(t("claimShop.notFound"));
            return res.json();
        },
        enabled: !!params?.id,
    });

    const handlePay = async () => {
        if (!shop) return;

        if (!isLoggedIn) {
            toast({
                title: t("account.loginRequired"),
                description: t("claimShop.loginRequiredDesc"),
                variant: "destructive"
            });
            setLocation("/login");
            return;
        }

        setLoading(true);
        try {
            let authHeader = await getAuthHeader();

            // If header is missing, try refreshing session
            if (!authHeader) {
                console.log("No auth header, attempting refresh...");
                await refreshAuth();
                authHeader = await getAuthHeader();
            }

            if (!authHeader) {
                throw new Error(t("claimShop.sessionExpired"));
            }

            const res = await fetch(`/api/stripe/checkout/shop-verification/${shop.id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader,
                },
                body: JSON.stringify({ countryCode }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || t("claimShop.paymentLinkFailed"));
            }

            const { url } = await res.json();
            if (url) {
                window.location.href = url;
            } else {
                throw new Error(t("claimShop.noPaymentLink"));
            }
        } catch (error: any) {
            toast({
                title: t("common.error"),
                description: error.message,
                variant: "destructive",
            });
            setLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (!shop) return (
        <div className="min-h-screen flex flex-col bg-background">

            <main className="flex-1 container mx-auto px-4 py-8 text-center">
                <h1 className="text-2xl font-bold">{t("claimShop.notFound")}</h1>
            </main>
            <Footer />
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-background">

            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <Card>
                        <CardHeader className="text-center">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Store className="h-8 w-8 text-primary" />
                            </div>
                            <CardTitle className="text-2xl">{t("claimShop.title")}</CardTitle>
                            <p className="text-muted-foreground mt-2">
                                {t("claimShop.aboutToClaim")} <strong>{shop.name}</strong> {t("claimShop.aboutToClaimSuffix")}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="bg-muted p-4 rounded-lg space-y-3">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    {t("claimShop.confirmDetails")}
                                </h3>
                                <div className="space-y-1 text-sm">
                                    <p className="font-medium">{shop.name}</p>
                                    <p className="text-muted-foreground">{shop.address}</p>
                                    <p className="text-muted-foreground">{shop.postal_code || ""} {shop.city}</p>
                                </div>
                                <p className="text-xs text-muted-foreground italic">
                                    {t("claimShop.addressNotice")}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-sm">{t("claimShop.featureLetterTitle")}</h4>
                                        <p className="text-xs text-muted-foreground">{t("claimShop.featureLetterDesc")}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-sm">{t("claimShop.featureOwnerTitle")}</h4>
                                        <p className="text-xs text-muted-foreground">{t("claimShop.featureOwnerDesc")}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-6">
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-lg font-medium">{t("claimShop.oneTimeCost")}</span>
                                    <span className="text-2xl font-bold">€{(getPrice("shop_verification", countryCode)?.amount ?? 19.95).toFixed(2).replace(".", ",")}</span>
                                </div>

                                <Button
                                    className="w-full h-12 text-lg"
                                    onClick={handlePay}
                                    disabled={loading}
                                >
                                    {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                                    {t("claimShop.goToPayment")}
                                </Button>

                                <p className="text-center text-xs text-muted-foreground mt-4">
                                    {t("claimShop.stripeNotice")}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
            <Footer />
        </div>
    );
}
